/**
 * @file components/staff-profile/EmploymentTab.tsx
 * @description 聘用資格分頁。
 *
 * 兩個區塊：
 *  1. 聘用合約表：展示聘用記錄（到職日/合約起迄/狀態/備注）+ 新增/編輯/刪除。
 *     合約到期前 90 日，整行以紅色高亮警告。
 *     端點：POST/PATCH/DELETE /api/settings/staff/:id/employments[/:eid]
 *
 *  2. 職稱×聘用職務表：展示資格記錄（職稱/聘用職務/是否有效）+ 新增/刪除。
 *     端點：POST/PATCH/DELETE /api/settings/staff/:id/qualifications[/:qid]
 */

import { useState } from 'react';
import {
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Switch,
} from 'antd';
import { message } from '@/lib/antd-app';
import { Plus, Edit3, Trash2, Briefcase, Award } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EMPLOYMENT_STATUS_LABELS } from '@zhcb/shared';
import type { EmploymentStatus } from '@zhcb/shared';
import { apiClient } from '@/lib/api-client';
import { SectionCard } from '@/components/shell';
import { useJobTitles, useJobFunctions } from '@/hooks/useClientLookups';
import type { ColumnsType } from 'antd/es/table';

// ─── 型別 ─────────────────────────────────────────────────────────────────────

/** 聘用合約記錄（後端回傳） */
export interface Employment {
  id: number;
  hiredAt: string;
  resignedAt?: string | null;
  contractStartAt: string;
  contractEndAt: string;
  status: EmploymentStatus;
  notes?: string | null;
}

/** 職稱×聘用職務記錄（後端回傳） */
export interface Qualification {
  id: number;
  jobTitle: string;
  jobFunction: string;
  isActive: boolean;
}

/** 聘用合約表單值 */
interface EmploymentFormValues {
  hiredAt: dayjs.Dayjs;
  resignedAt?: dayjs.Dayjs | null;
  contractStartAt: dayjs.Dayjs;
  contractEndAt: dayjs.Dayjs;
  status: EmploymentStatus;
  notes?: string | null;
}

/** 職稱職務表單值 */
interface QualificationFormValues {
  jobTitle: string;
  jobFunction: string;
  isActive: boolean;
}

// ─── 常數：到期前 90 天警示閾值 ───────────────────────────────────────────────

const EXPIRY_WARNING_DAYS = 90;

/** 判斷合約是否即將到期（距今 90 天內） */
function isExpiringSoon(contractEndAt: string): boolean {
  const end = dayjs(contractEndAt);
  const diff = end.diff(dayjs(), 'day');
  return diff >= 0 && diff <= EXPIRY_WARNING_DAYS;
}

// ─── 聘用合約區塊 ─────────────────────────────────────────────────────────────

/**
 * 聘用合約子組件。
 * 包含新增/編輯 Modal 和刪除 Popconfirm。
 */
function EmploymentSection({ userId, employments }: { userId: number; employments: Employment[] }) {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Employment | null>(null);
  const [empForm] = Form.useForm<EmploymentFormValues>();

  /** 新增聘用合約 */
  const createEmp = useMutation({
    mutationFn: (values: EmploymentFormValues) =>
      apiClient.post(`/api/settings/staff/${userId}/employments`, {
        hiredAt: values.hiredAt.format('YYYY-MM-DD'),
        resignedAt: values.resignedAt?.format('YYYY-MM-DD') ?? null,
        contractStartAt: values.contractStartAt.format('YYYY-MM-DD'),
        contractEndAt: values.contractEndAt.format('YYYY-MM-DD'),
        status: values.status,
        notes: values.notes ?? null,
      }),
    onSuccess: () => {
      message.success('聘用合約已新增');
      void qc.invalidateQueries({ queryKey: ['staff-profile', userId] });
      closeModal();
    },
    onError: (err) => {
      message.error(err instanceof Error ? err.message : '新增失敗');
    },
  });

  /** 更新聘用合約 */
  const updateEmp = useMutation({
    mutationFn: (values: EmploymentFormValues) =>
      apiClient.patch(`/api/settings/staff/${userId}/employments/${editing!.id}`, {
        hiredAt: values.hiredAt.format('YYYY-MM-DD'),
        resignedAt: values.resignedAt?.format('YYYY-MM-DD') ?? null,
        contractStartAt: values.contractStartAt.format('YYYY-MM-DD'),
        contractEndAt: values.contractEndAt.format('YYYY-MM-DD'),
        status: values.status,
        notes: values.notes ?? null,
      }),
    onSuccess: () => {
      message.success('聘用合約已更新');
      void qc.invalidateQueries({ queryKey: ['staff-profile', userId] });
      closeModal();
    },
    onError: (err) => {
      message.error(err instanceof Error ? err.message : '更新失敗');
    },
  });

  /** 刪除聘用合約 */
  const deleteEmp = useMutation({
    mutationFn: (eid: number) =>
      apiClient.delete(`/api/settings/staff/${userId}/employments/${eid}`),
    onSuccess: () => {
      message.success('聘用合約已刪除');
      void qc.invalidateQueries({ queryKey: ['staff-profile', userId] });
    },
    onError: (err) => {
      message.error(err instanceof Error ? err.message : '刪除失敗');
    },
  });

  /** 開啟新增 Modal */
  function openAdd() {
    setEditing(null);
    empForm.resetFields();
    empForm.setFieldsValue({ status: 'active' });
    setModalOpen(true);
  }

  /** 開啟編輯 Modal，預填欄位 */
  function openEdit(record: Employment) {
    setEditing(record);
    empForm.setFieldsValue({
      hiredAt: dayjs(record.hiredAt),
      resignedAt: record.resignedAt ? dayjs(record.resignedAt) : null,
      contractStartAt: dayjs(record.contractStartAt),
      contractEndAt: dayjs(record.contractEndAt),
      status: record.status,
      notes: record.notes ?? null,
    });
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditing(null);
    empForm.resetFields();
  }

  /** 表格欄位定義（含 90 天到期警示） */
  const columns: ColumnsType<Employment> = [
    {
      title: '到職日期',
      dataIndex: 'hiredAt',
      width: 110,
      // 格式化 ISO timestamp 為 YYYY-MM-DD 顯示
      render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD') : '—'),
    },
    {
      title: '合約起始',
      dataIndex: 'contractStartAt',
      width: 110,
      // 格式化 ISO timestamp 為 YYYY-MM-DD 顯示
      render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD') : '—'),
    },
    {
      title: '合約終止',
      dataIndex: 'contractEndAt',
      width: 110,
      // 即將到期（90 天內）時顯示紅色警示；格式化 ISO timestamp 為 YYYY-MM-DD
      render: (v: string) => (
        <span
          style={{
            color: isExpiringSoon(v) ? '#cf1322' : undefined,
            fontWeight: isExpiringSoon(v) ? 600 : undefined,
          }}
        >
          {v ? dayjs(v).format('YYYY-MM-DD') : '—'}
          {isExpiringSoon(v) && (
            <Tag color="red" style={{ marginLeft: 6, fontSize: 11 }}>
              即將到期
            </Tag>
          )}
        </span>
      ),
    },
    {
      title: '狀態',
      dataIndex: 'status',
      width: 90,
      render: (v: EmploymentStatus) => {
        const color = v === 'active' ? 'success' : v === 'suspended' ? 'warning' : 'default';
        return <Tag color={color}>{EMPLOYMENT_STATUS_LABELS[v] ?? v}</Tag>;
      },
    },
    {
      title: '備注',
      dataIndex: 'notes',
      ellipsis: true,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: '離職日',
      dataIndex: 'resignedAt',
      width: 110,
      // 格式化 ISO timestamp 為 YYYY-MM-DD 顯示
      render: (v: string | null) => (v ? dayjs(v).format('YYYY-MM-DD') : '—'),
    },
    {
      title: '操作',
      key: '_actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size={4}>
          <Button
            type="text"
            size="small"
            icon={<Edit3 size={13} />}
            onClick={() => openEdit(record)}
          />
          <Popconfirm
            title="確定刪除此聘用合約？"
            onConfirm={() => deleteEmp.mutate(record.id)}
            okText="刪除"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button type="text" size="small" danger icon={<Trash2 size={13} />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <SectionCard
        title="聘用合約"
        icon={<Briefcase size={14} />}
        padless
        actions={
          <Button type="primary" size="small" icon={<Plus size={13} />} onClick={openAdd}>
            新增聘用
          </Button>
        }
      >
        <Table<Employment>
          rowKey="id"
          size="small"
          dataSource={employments}
          columns={columns}
          scroll={{ x: 700 }}
          pagination={false}
          // 即將到期的行整行紅色高亮
          rowClassName={(record) =>
            isExpiringSoon(record.contractEndAt) ? 'employment-expiring-row' : ''
          }
        />
      </SectionCard>

      {/* 新增/編輯 Modal */}
      <Modal
        title={editing ? '編輯聘用合約' : '新增聘用合約'}
        open={modalOpen}
        onCancel={closeModal}
        footer={null}
        width={640}
        destroyOnHidden
      >
        <Form<EmploymentFormValues>
          form={empForm}
          layout="vertical"
          style={{ marginTop: 16 }}
          onFinish={(values) => (editing ? updateEmp.mutate(values) : createEmp.mutate(values))}
        >
          <Form.Item
            name="hiredAt"
            label="到職日期"
            rules={[{ required: true, message: '請選擇到職日期' }]}
          >
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item name="resignedAt" label="離職日期">
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item
            name="contractStartAt"
            label="合約起始日期"
            rules={[{ required: true, message: '請選擇合約起始日期' }]}
          >
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item
            name="contractEndAt"
            label="合約終止日期"
            rules={[{ required: true, message: '請選擇合約終止日期' }]}
          >
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item
            name="status"
            label="聘用狀態"
            rules={[{ required: true, message: '請選擇聘用狀態' }]}
          >
            <Select
              options={Object.entries(EMPLOYMENT_STATUS_LABELS).map(([v, l]) => ({
                value: v,
                label: l,
              }))}
            />
          </Form.Item>
          <Form.Item name="notes" label="備注">
            <Input.TextArea rows={2} maxLength={500} showCount />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={createEmp.isPending || updateEmp.isPending}
            >
              {editing ? '儲存' : '新增'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* 即將到期行紅色 CSS：通過全局 style 注入，避免 CSS modules */}
      <style>{`
        .employment-expiring-row td {
          background-color: #fff2f0 !important;
        }
        .employment-expiring-row:hover td {
          background-color: #ffdbdb !important;
        }
      `}</style>
    </>
  );
}

// ─── 職稱×聘用職務區塊 ────────────────────────────────────────────────────────

/**
 * 職稱與聘用職務子組件。
 * 職稱來自 job-titles 字典；聘用職務來自 job-functions 字典。
 */
function QualificationSection({
  userId,
  qualifications,
}: {
  userId: number;
  qualifications: Qualification[];
}) {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Qualification | null>(null);
  const [qualForm] = Form.useForm<QualificationFormValues>();

  /** 職稱字典 */
  const { data: jobTitlesData } = useJobTitles({ activeOnly: true });
  /** 聘用職務字典 */
  const { data: jobFunctionsData } = useJobFunctions({ activeOnly: true });

  /** 新增職稱職務 */
  const createQual = useMutation({
    mutationFn: (values: QualificationFormValues) =>
      apiClient.post(`/api/settings/staff/${userId}/qualifications`, values),
    onSuccess: () => {
      message.success('職稱職務已新增');
      void qc.invalidateQueries({ queryKey: ['staff-profile', userId] });
      closeModal();
    },
    onError: (err) => {
      message.error(err instanceof Error ? err.message : '新增失敗');
    },
  });

  /** 更新職稱職務 */
  const updateQual = useMutation({
    mutationFn: (values: QualificationFormValues) =>
      apiClient.patch(`/api/settings/staff/${userId}/qualifications/${editing!.id}`, values),
    onSuccess: () => {
      message.success('職稱職務已更新');
      void qc.invalidateQueries({ queryKey: ['staff-profile', userId] });
      closeModal();
    },
    onError: (err) => {
      message.error(err instanceof Error ? err.message : '更新失敗');
    },
  });

  /** 刪除職稱職務 */
  const deleteQual = useMutation({
    mutationFn: (qid: number) =>
      apiClient.delete(`/api/settings/staff/${userId}/qualifications/${qid}`),
    onSuccess: () => {
      message.success('職稱職務已刪除');
      void qc.invalidateQueries({ queryKey: ['staff-profile', userId] });
    },
    onError: (err) => {
      message.error(err instanceof Error ? err.message : '刪除失敗');
    },
  });

  function openAdd() {
    setEditing(null);
    qualForm.resetFields();
    qualForm.setFieldsValue({ isActive: true });
    setModalOpen(true);
  }

  function openEdit(record: Qualification) {
    setEditing(record);
    qualForm.setFieldsValue(record);
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditing(null);
    qualForm.resetFields();
  }

  const columns: ColumnsType<Qualification> = [
    {
      title: '職稱',
      dataIndex: 'jobTitle',
      ellipsis: true,
    },
    {
      title: '聘用職務',
      dataIndex: 'jobFunction',
      ellipsis: true,
    },
    {
      title: '有效',
      dataIndex: 'isActive',
      width: 70,
      render: (v: boolean) => <Tag color={v ? 'success' : 'default'}>{v ? '有效' : '失效'}</Tag>,
    },
    {
      title: '操作',
      key: '_actions',
      width: 90,
      fixed: 'right',
      render: (_, record) => (
        <Space size={4}>
          <Button
            type="text"
            size="small"
            icon={<Edit3 size={13} />}
            onClick={() => openEdit(record)}
          />
          <Popconfirm
            title="確定刪除此職稱職務記錄？"
            onConfirm={() => deleteQual.mutate(record.id)}
            okText="刪除"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button type="text" size="small" danger icon={<Trash2 size={13} />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <SectionCard
        title="職稱與聘用職務"
        icon={<Award size={14} />}
        padless
        actions={
          <Button type="primary" size="small" icon={<Plus size={13} />} onClick={openAdd}>
            新增職務
          </Button>
        }
      >
        <Table<Qualification>
          rowKey="id"
          size="small"
          dataSource={qualifications}
          columns={columns}
          pagination={false}
        />
      </SectionCard>

      <Modal
        title={editing ? '編輯職稱職務' : '新增職稱職務'}
        open={modalOpen}
        onCancel={closeModal}
        footer={null}
        width={480}
        destroyOnHidden
      >
        <Form<QualificationFormValues>
          form={qualForm}
          layout="vertical"
          style={{ marginTop: 16 }}
          onFinish={(values) => (editing ? updateQual.mutate(values) : createQual.mutate(values))}
        >
          <Form.Item
            name="jobTitle"
            label="職稱"
            rules={[{ required: true, message: '請選擇職稱' }]}
          >
            <Select
              showSearch
              placeholder="請選擇職稱"
              filterOption={(input, opt) =>
                ((opt?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={(jobTitlesData?.items ?? []).map((item) => ({
                value: item.name,
                label: item.name,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="jobFunction"
            label="聘用職務"
            rules={[{ required: true, message: '請選擇聘用職務' }]}
          >
            <Select
              showSearch
              placeholder="請選擇聘用職務"
              filterOption={(input, opt) =>
                ((opt?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={(jobFunctionsData?.items ?? []).map((item) => ({
                value: item.name,
                label: item.name,
              }))}
            />
          </Form.Item>
          <Form.Item name="isActive" label="是否有效" valuePropName="checked">
            <Switch defaultChecked />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={createQual.isPending || updateQual.isPending}
            >
              {editing ? '儲存' : '新增'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

// ─── 主組件 ──────────────────────────────────────────────────────────────────

export interface EmploymentTabProps {
  /** 員工 userId */
  userId: number;
  /** 聘用合約記錄列表（來自 profile 查詢） */
  employments: Employment[];
  /** 職稱職務記錄列表（來自 profile 查詢） */
  qualifications: Qualification[];
}

/**
 * 聘用資格 Tab 主組件。
 * 上半：聘用合約（含 90 天到期警示）。
 * 下半：職稱×聘用職務。
 */
export function EmploymentTab({ userId, employments, qualifications }: EmploymentTabProps) {
  return (
    <Space direction="vertical" size={16} style={{ display: 'flex' }}>
      <EmploymentSection userId={userId} employments={employments} />
      <QualificationSection userId={userId} qualifications={qualifications} />
    </Space>
  );
}
